import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  psychologistInspectionAPI,
  updatePsychologistInspectionAPI,
} from "../../../../services/psychologist.services";
import { Button, Col, Form, message, Modal, Row, Tabs } from "antd";
import { useLocation } from "react-router-dom";
import PsychologistPatientDemographics from "../../InspectionForms/HistoryForm/PsychologistPatientDemographics";
import PreviousCounselling from "../../InspectionForms/PreviousCounselling/PreviousCounselling";
import Symptoms from "../../InspectionForms/Symptoms/Symptoms";
import PsychologistDiagnosis from "../../InspectionForms/Symptoms/PsychologistDiagnosis";
import PsychologistTherapiesSuggested from "../../InspectionForms/Therapy/PsychologistTherapiesSuggested";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import { PsychologistInitialContext } from "./PsychologistInitialAssessmentProvider";
import useIsMobile from "../../../../hooks/useIsMobile";
import "../../ipforms.css";
import "../../Doctor/InitialInspection/InitialInspectionHeader.css";
const PsychologistInitialInspectionForm = ({
  setVisible,
  refreshPage,
  initialRefresh,
  editId,
  patientData,
  setConfirmModal,
  activeTab,
  setActiveTab,
}) => {
  const isMobile = useIsMobile();
  const formBodyRef = useRef(null);
  const location = useLocation();
  const params = Object.fromEntries(new URLSearchParams(location?.search));
  const [demographicForm] = Form.useForm();
  const [counsellingForm] = Form.useForm();
  const [symptomsForm] = Form.useForm();
  const [diagnosisForm] = Form.useForm();
  const [therapyForm] = Form.useForm();
  const [buttonPressed, setButtonPressed] = useState(false);
  const { formData } = useContext(PsychologistInitialContext);
  const [isDirty, setIsDirty] = useState(false);

  const tabItems = useMemo(
    () => [
      {
        key: "1",
        label: isMobile ? "Demographics" : "Patient Demographics",
      },
      {
        key: "2",
        label: isMobile ? "Counselling" : "Previous Counselling / Psychotherapy",
      },
      {
        key: "3",
        label: isMobile ? "Symptoms" : "Symptoms / Impressions",
      },
      {
        key: "4",
        label: isMobile ? "Diagnosis" : "Psychology Diagnosis",
      },
      {
        key: "5",
        label: isMobile ? "Therapies" : "Therapies Suggested",
      },
    ],
    [isMobile]
  );

  useEffect(() => {
    if (formBodyRef.current) {
      formBodyRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  const handleSubmit = async (values, flag) => {
    try {
      if (editId) {
        await updatePsychologistInspectionAPI(editId, values);
        initialRefresh();
      } else {
        await psychologistInspectionAPI(values);
        refreshPage();
        initialRefresh();
      }

      message.success(`${flag} submitted successfully`);
      if (buttonPressed) {
        if (activeTab === "5") {
          setVisible(false);
        } else {
          const nextTab = (parseInt(activeTab) + 1).toString();
          setActiveTab(nextTab);
        }
        setButtonPressed(false);
      }
      setIsDirty(false);
    } catch (error) {
      console.log(error, "Error while submitting");
      message.error(error?.response?.data?.message || "Something went wrong");
    }
  };

  const onDemographicSubmit = (values) => {
    handleSubmit(
      {
        patient_demographics: values,
        admission_id: params?.patient,
      },
      "Patient Demographics"
    );
  };

  const onCounsellingSubmit = (values) => {
    handleSubmit(
      {
        previous_counselling: values,
        admission_id: params?.patient,
      },
      "Previous Counselling"
    );
  };
  const onSymptomsSubmit = (values) => {
    handleSubmit(
      {
        symptoms: values,
        admission_id: params?.patient,
      },
      "Symptoms/ Impressions"
    );
  };
  const onDiagnosisSubmit = (values) => {
    handleSubmit(
      {
        psychology_diagnosis: values,
        admission_id: params?.patient,
      },
      "Psychology Diagnosis"
    );
  };
  const onTherapySubmit = (values) => {
    handleSubmit(
      {
        therapies_suggested: values,
        admission_id: params?.patient,
      },
      "Therapies Suggested"
    );
  };

  const getFormKey = () => {
    switch (activeTab) {
      case "1":
        return demographicForm.submit();
      case "2":
        return counsellingForm.submit();
      case "3":
        return symptomsForm.submit();
      case "4":
        return diagnosisForm.submit();
      case "5":
        return therapyForm.submit();
      default:
        return "";
    }
  };

  const handleTabChange = (key) => {
    if (isDirty) {
      Modal.confirm({
        title: "Unsaved changes",
        content:
          "You have unsaved changes. Are you sure you want to save these changes?",
        onOk: () => {
          switch (activeTab) {
            case "1":
              demographicForm.validateFields().then((values) => {
                setIsDirty(false);
                onDemographicSubmit(values);
                setActiveTab(key);
              });
              break;
            case "2":
              counsellingForm.validateFields().then((values) => {
                setIsDirty(false);
                onCounsellingSubmit(values);
                setActiveTab(key);
              });
              break;
            case "3":
              symptomsForm.validateFields().then((values) => {
                setIsDirty(false);
                onSymptomsSubmit(values);
                setActiveTab(key);
              });
              break;
            case "4":
              diagnosisForm.validateFields().then((values) => {
                setIsDirty(false);
                onDiagnosisSubmit(values);
                setActiveTab(key);
              });
              break;
            case "5":
              therapyForm.validateFields().then((values) => {
                setIsDirty(false);
                onTherapySubmit(values);
                setActiveTab(key);
              });
              break;
            default:
              setActiveTab(key);
          }
        },
        onCancel: () => {
          setActiveTab(key);
          setIsDirty(false);
        },
      });
    } else {
      setActiveTab(key);
    }
  };

  const onFormValuesChange = () => {
    setIsDirty(true);
  };

  return (
    <div className="initial-inspection-form-wrapper">
      <div className="ia-form-tabs">
        <Row>
          <Col
            xs={24}
            sm={24}
            md={{ span: 23, offset: 1 }}
            lg={{ span: 21, offset: 3 }}
            xl={{ span: 19, offset: 5 }}
            xxl={{ span: 18, offset: 6 }}
          >
            <Tabs
              activeKey={activeTab}
              items={tabItems}
              onChange={handleTabChange}
            />
          </Col>
        </Row>
      </div>
      <div className="ia-form-panel">
        <div className="ia-form-body" ref={formBodyRef}>
          {activeTab === "1" && (
            <Form
              id="patient_demographics-form"
              form={demographicForm}
              layout="vertical"
              onFinish={onDemographicSubmit}
              onValuesChange={onFormValuesChange}
              initialValues={formData}
              className="ip-forms"
            >
              <PsychologistPatientDemographics
                form={demographicForm}
                formData={formData}
              />
            </Form>
          )}
          {activeTab === "2" && (
            <Form
              id="previous-counselling-form"
              form={counsellingForm}
              layout="vertical"
              onFinish={onCounsellingSubmit}
              onValuesChange={onFormValuesChange}
              initialValues={formData}
              className="ip-forms"
            >
              <PreviousCounselling
                form={counsellingForm}
                formData={formData}
                context={PsychologistInitialContext}
              />
            </Form>
          )}
          {activeTab === "3" && (
            <Form
              id="symptoms-form"
              form={symptomsForm}
              layout="vertical"
              onFinish={onSymptomsSubmit}
              onValuesChange={onFormValuesChange}
              // initialValues={formData}
              className="ip-forms"
            >
              <Symptoms form={symptomsForm} formData={formData} />
            </Form>
          )}

          {activeTab === "4" && (
            <Form
              id="psychology-diagnosis-form"
              form={diagnosisForm}
              layout="vertical"
              onFinish={onDiagnosisSubmit}
              onValuesChange={onFormValuesChange}
              className="ip-forms"
            >
              <PsychologistDiagnosis
                form={diagnosisForm}
                context={PsychologistInitialContext}
              />
            </Form>
          )}
          {activeTab === "5" && (
            <Form
              id="therapies-suggested-form"
              form={therapyForm}
              layout="vertical"
              onFinish={onTherapySubmit}
              onValuesChange={onFormValuesChange}
              initialValues={formData}
              className="ip-forms"
            >
              <PsychologistTherapiesSuggested
                form={therapyForm}
                context={PsychologistInitialContext}
              />
            </Form>
          )}
        </div>
        <div className="ia-form-footer">
          <Button
            onClick={() => {
              getFormKey();
              setButtonPressed(true);
              document
                .querySelector(".initial-inspection-form-wrapper")
                ?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
            }}
            type="primary"
            className={btnStyles.mdBtn}
            style={{ fontWeight: 500 }}
          >
            {activeTab === "5" ? "Done" : " Save & Proceed"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PsychologistInitialInspectionForm;
